import Highcharts from 'highcharts';
import { Component, effect, inject, input } from '@angular/core';
import { MathComputationService } from 'src/app/services/math-computation.service';
import { BRIGHT_GREEN, CYAN, GROSVENOR_BLUE, WHITE } from '@gcm/gcm-ui-angular';
import dayjs from 'dayjs';
import { BaseWidgetComponent } from '../../base-widget/base-widget.component';
import { GcmChartComponent } from '../../gcm-chart/gcm-chart.component';
import { AggregatedPortfolioSeries } from 'src/app/models/classes/aggregated-portfolio-series.class';
import { InvestorDashboardPortfolioSeriesBalanceSummary } from 'src/app/models/classes/investor-dashboard.class';
import { DataTableCore } from 'highcharts/highmaps';

@Component({
  selector: 'app-series-ccd-widget',
  imports: [
    BaseWidgetComponent,
    GcmChartComponent,
  ],
  templateUrl: './series-ccd-widget.component.html',
  styleUrl: './series-ccd-widget.component.scss'
})
export class SeriesCcdWidgetComponent {
  private mathService = inject(MathComputationService);

  readonly series = input.required<AggregatedPortfolioSeries>();
  readonly portfolioId = input.required<number>();
  readonly currency = input.required<string>();
  readonly selectedAsOfDate = input.required<string>();
  readonly isMobileDevice = input<boolean>(false);
  chartConfig!: DataTableCore;
  cumulativeChartStyle = { width: '100%', display: 'flex' };

  private readonly rebuildChart = effect(() => {
    this.series();
    this.selectedAsOfDate();
    this.isMobileDevice();
    if (this.series()) {
      this.buildWidget();
    }
  });

  buildWidget() {
    const chartData = this.mathService.filterChartDataBeforeSpecificYear(this.series().portfolioSeriesBalanceSummary);
    chartData.forEach(element => {
      element.disNavSum = (element.distributions as number) + (element.nav as number);
      element.month = new Date(element.reportingPeriod).getMonth() + 1;
      element.year = new Date(element.reportingPeriod).getFullYear();
      const quarter = element.month / 3
      element.quarter = `Q${quarter}<br/>${element['year']}`
    });
    let sortedData = chartData.sort((a, b) => dayjs(a.reportingPeriod).valueOf() - (dayjs(b.reportingPeriod)).valueOf());
    let navDisConChartData: InvestorDashboardPortfolioSeriesBalanceSummary[];
    if (sortedData) {
      sortedData = sortedData.filter(data => new Date(data['reportingPeriod'].slice(0, 10)) <= new Date(this.selectedAsOfDate()));
    }
    if (sortedData && sortedData.length >= 16) {
      const recentQuarterValues = sortedData.slice(Math.max(sortedData.length - 4, 0));
      navDisConChartData = sortedData.filter(data => data['month'] === 12 && !recentQuarterValues.includes(data)).concat(recentQuarterValues);
    }
    else {
      navDisConChartData = sortedData;
    }

    this.chartConfig = {
      chart: {
        height: 197,
        borderRadius: 8,
        backgroundColor: '#fbfbfb'
      },
      xAxis: {
        categories: navDisConChartData.map(x => x.quarter),
        tickLength: 10,
        tickWidth: 1,
        tickPosition: 'outside',
        labels: {
          staggerLines: this.isMobileDevice() ? 2 : 0,
          style: {
            fontSize: this.isMobileDevice() ? '10px' : '12px'
          }
        }
      },
      yAxis: {
        title: {
          text: ''
        },
        labels: {
          align: 'right',
          reserveSpace: true,
          formatter: function (context: Highcharts.AxisLabelsFormatterContextObject) {
            return Number(context.value) != 0 ? Math.round((Number(context.value) * 100) / 1000000) / 100 + 'M' : context.value.toString();
          },
          style: {
            fontSize: '12px'
          }
        },
        maxPadding: 0.25,
        tickAmount: 3
      },
      title: {
        text: ''
      },
      legend: {
        shadow: false,
        align: 'center',
        verticalAlign: 'top',
        layout: 'vertical',
        itemStyle: {
          fontWeight: '400'
        },
        events: {
          itemClick: () => false
        }
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        line: {
          shadow: false,
          borderWidth: 0,
          states: {
            hover: {
              enabled: false
            },
            inactive: {
              opacity: 1
            }
          }
        },
        area: {
          shadow: false,
          states: {
            hover: {
              enabled: false
            },
            inactive: {
              opacity: 1
            }
          }
        }
      },
      series: [{
        data: navDisConChartData.map(x => x.disNavSum),
        type: 'area',
        name: 'NAV + Distributions',
        color: Highcharts.color(CYAN).tweenTo(Highcharts.color(WHITE), 0.7) as string,
        lineColor: GROSVENOR_BLUE,
        lineWidth: 1.5,
        marker: {
          enabled: true,
          fillColor: GROSVENOR_BLUE,
          radius: 3
        }
      },
      {
        data: navDisConChartData.map(x => x.contributions),
        type: 'line',
        name: 'Contributions',
        color: BRIGHT_GREEN,
        lineWidth: 3,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 3
        }
      }]
    }
  }
}